import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Options } from 'highcharts';
import Highcharts from 'highcharts/highstock';
import { renderToStaticMarkup } from 'react-dom/server';
import { defaultPeriods } from './InteractiveNavChart.constants';
import { ConfigType, differenceBetweenTwoDate, getDate } from '@/utils/date';
import {
  TNavChartPeriod,
  TNavChartPeriods,
  TNavChartRenderTooltip,
  TNavChartSeries,
} from './InteractiveNavChart.types';

export interface IUseInteractiveNavCharOptionsProps {
  series: TNavChartSeries;
  renderTooltip: TNavChartRenderTooltip;
  fillColor?: string;
  yAxisTitle?: string;
  xAxis?: Options['xAxis'];
}

export const useInteractiveNavCharOptions = ({
  series,
  renderTooltip,
  fillColor,
  yAxisTitle = 'ریال',
  xAxis,
}: IUseInteractiveNavCharOptionsProps) => {
  useEffect(() => {
    Highcharts.setOptions({
      lang: { rangeSelectorZoom: 'بازه' },
      chart: { style: { fontFamily: 'dana' } },
    });
  }, []);

  const tooltipFormatter = useCallback(
    function () {
      // @ts-ignore
      return renderToStaticMarkup(renderTooltip(this) as ReactElement);
    },
    [renderTooltip]
  );

  return useMemo(
    () => ({
      accessibility: {
        enabled: false,
      },

      chart: { height: 250, style: { fontFamily: 'dana' } },
      credits: { enabled: false },
      scrollbar: { enabled: false },
      navigator: { enabled: false },
      rangeSelector: { enabled: false },
      series: series.map((item) => ({
        name: item.name,
        type: item.type,
        color: item.color,
        data: item.data ?? [],
        threshold: null,
        colorIndex: 1,
        marker: {
          width: 6,
          height: 6,
          symbol: 'circle',
          fillColor: item.color,
        },
        tooltip: {
          valueDecimals: 2,
        },
      })),
      xAxis: {
        type: 'date',
        ordinal: false,
        labels: {
          formatter: function (): string {
            return new Date((this as any).value).toLocaleDateString('fa-IR');
          },
        },
        ...xAxis,
      },
      yAxis: [
        {
          opposite: false,
          gridLineWidth: 0,

          title: {
            text: yAxisTitle,
            align: 'low',
            rotation: 0,
            x: -20,
            y: 17,
            style: {
              fontSize: '11px',
              color: '#BAC5CD',
            },
          },
          offset: 20,
          labels: {
            formatter: function (): string {
              return (this as any)?.value.toLocaleString();
            },
            x: -30,
            y: -5,
            style: {
              color: '#838A9A',
              fontWeight: 400,
            },
            align: 'left',
          },
        },
      ],
      tooltip: {
        shared: false,
        useHTML: true,
        borderColor: '#F2F5F6',
        backgroundColor: '#fff',
        borderRadius: 8,
        shape: 'rect',
        style: {
          color: '#444954',
          fontWeight: 500,
        },
        formatter: tooltipFormatter,
      },
      plotOptions: {
        area: {
          lineWidth: 2,
          states: {
            hover: {
              lineWidth: 2,
            },
          },
          fillColor: {
            linearGradient: [0, 0, 0, 200],
            stops: [
              [0, fillColor],
              [1, '#fff'],
            ],
          },
        },
        series: {
          dataGrouping: {
            enabled: false,
          },
        },
      },
    }),
    [series, tooltipFormatter]
  );
};

export const usePeriods = (
  periods: TNavChartPeriods = defaultPeriods,
  defaultSelectedIndex: number = 0
) => {
  if (defaultSelectedIndex > periods.length - 1) {
    defaultSelectedIndex = 0;
  }

  const [selectedPeriod, setSelectedPeriod] = useState<TNavChartPeriod>(
    periods[defaultSelectedIndex]
  );
  return { selectedPeriod, setSelectedPeriod, periods };
};

export interface UseDisabledNotCompletedPeriodsProps {
  periods?: TNavChartPeriods;
  startDate?: ConfigType;
}
export const useDisabledNotCompletedPeriods = ({
  periods,
  startDate,
}: UseDisabledNotCompletedPeriodsProps) => {
  return useMemo(() => {
    const maxMonthAgo = startDate
      ? differenceBetweenTwoDate(getDate(), startDate, 'month', true)
      : null;

    if (!maxMonthAgo) {
      return periods;
    }

    return (periods || []).map((period) => ({
      ...period,
      disabled: maxMonthAgo < period.month,
    }));
  }, [periods, startDate]) as TNavChartPeriods;
};
